import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { Box } from 'pcln-design-system'

const noop = () => {}

// Element that creates the shadow effect style if enabled, or render an empty div if not
const ShadowEffectElement = styled(Box)`
  z-index: 10;
  display: block;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(51, 51, 51, 0.65);
  pointer-events: none;
`

class ShadowEffect extends React.PureComponent {
  constructor(props) {
    super(props)
    this.state = {
      isEnabled: false
    }

    this.disableShadowState = () => this.setState(() => ({ isEnabled: false }))
    this.enableShadowState = () => this.setState(() => ({ isEnabled: true }))

    this.handleFocus = this.enableShadowState
    this.handleBlur = this.props.alternativeBlurHandling
      ? this.handleAlternativeBlur.bind(this)
      : this.disableShadowState
  }

  componentDidMount() {
    if (this.props.alternativeBlurHandling) {
      document.addEventListener('focusin', this.handleDocumentClick, true)
      document.addEventListener('click', this.handleDocumentClick, true)
    }
  }

  componentWillUnmount() {
    if (this.props.alternativeBlurHandling) {
      document.removeEventListener('focusin', this.handleAlternativeBlur)
      document.removeEventListener('click', this.handleAlternativeBlur)
    }
  }

  handleDocumentClick = e => {
    if (e.target && this.node && !this.node.contains(e.target)) {
      this.disableShadowState()
    }
  }

  handleAlternativeBlur(e) {
    const target =
      e.nativeEvent.relatedTarget || e.nativeEvent.explicitOriginalTarget
    if (!target || !this.node.contains(target)) {
      this.setState(() => ({ isEnabled: false }))
    }
  }

  render() {
    const { forProperty, children, ...remainingProps } = this.props
    const { isEnabled } = this.state
    const { handleBlur, handleFocus, enableShadowState, disableShadowState } =
      this
    return (
      <Box
        width={1}
        onBlur={handleBlur}
        onFocus={handleFocus}
        {...remainingProps}
      >
        {isEnabled && <ShadowEffectElement />}
        <div
          ref={node => {
            this.node = node
          }}
          style={{
            width: '100%',
            position: 'relative',
            zIndex: isEnabled ? 20 : 'auto'
          }}
        >
          {typeof children === 'function'
            ? children({ isEnabled, enableShadowState, disableShadowState })
            : children}
        </div>
      </Box>
    )
  }
}

ShadowEffect.propTypes = {
  forProperty: PropTypes.string.isRequired,
  enabled: PropTypes.bool,
  alternativeBlurHandling: PropTypes.bool.isRequired,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]).isRequired
}

ShadowEffect.displayName = 'ShadowEffect'

ShadowEffect.defaultProps = {
  enabled: false
}

ShadowEffect.propTypes = {
  forProperty: PropTypes.string.isRequired,
  enabled: PropTypes.bool,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
    PropTypes.func
  ]).isRequired
}

const NoShadowEffect = ({ children }) => (
  <>
    {typeof children === 'function'
      ? children({
          isEnabled: false,
          enableShadowState: noop,
          disableShadowState: noop
        })
      : children}
  </>
)

NoShadowEffect.displayName = 'NoShadowEffect'

NoShadowEffect.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]).isRequired
}

const createShadowEffectWhen = condition =>
  condition
    ? props => <ShadowEffect {...props} />
    : props => <NoShadowEffect {...props} />

export { ShadowEffect as default, NoShadowEffect, createShadowEffectWhen }
